import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FiAlertTriangle, FiChevronRight, FiDownload, FiX } from "react-icons/fi";
import Header from "../Components/Header";
import "../assets/css/RiskPrediction.css";
import { getRiskModelInfo, getSurveySites, getWoredaRiskPredictions } from "../Services/apiService";
import ImputedNote from "../Components/ImputedNote";
import { ACCURACY_NOTE, accuracyValue } from "../Components/ModelPerformance";

// shown on the page, in the popup and in every downloaded row
const PREDICTION_STATUS = "Experimental – prospective validation only";

// predicted prevalence % -> the endemicity categories used on the
// Endemicity map and Hotspot pages
const ENDEMICITY = [
  { label: ">=50% prevalence (high)", min: 50 },
  { label: "10 - 49.9% prevalence (moderate)", min: 10 },
  { label: "1 - 9.9% prevalence (low)", min: 1 },
  { label: "<1% prevalence (non-endemic)", min: -Infinity },
];
// category of the % as shown (1 decimal), so 9.96 -> "10.0%" is moderate
const endemicityOf = (pct) =>
  pct === null || pct === undefined
    ? null
    : ENDEMICITY.find((e) => Number(Number(pct).toFixed(1)) >= e.min).label;
// soft pastel badges: light fill, darker text and border in the same hue
const BADGE_STYLES = {
  ">=50% prevalence (high)": { background: "#fde2e2", color: "#9b1c1c", borderColor: "#f8b4b4" },
  "10 - 49.9% prevalence (moderate)": { background: "#fef3c7", color: "#92400e", borderColor: "#fcd98a" },
  "1 - 9.9% prevalence (low)": { background: "#e0f2fe", color: "#075985", borderColor: "#a5d8f5" },
  "<1% prevalence (non-endemic)": { background: "#e7f6ec", color: "#166534", borderColor: "#b7e4c7" },
};
const NO_DATA_STYLE = { background: "#f1f5f9", color: "#475569", borderColor: "#e2e8f0" };
const colorOf = (label) => (BADGE_STYLES[label] || NO_DATA_STYLE).borderColor;

const fmt = (v, digits = 1, suffix = "") =>
  v === null || v === undefined || Number.isNaN(v)
    ? "—"
    : `${Number(v).toFixed(digits)}${suffix}`;

// when the stored prediction was made (older rows: when they were loaded)
const predictedAt = (r) => r.predicted_at || r.lastUpdated || null;
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—");
// first columns of the download; every other field follows as stored
const LEADING_COLUMNS = [
  "Sr No", "location_name", "woreda", "zone", "region", "location_id",
  "prevalence_pct", "Predicted_Prevalence_pct", "Predicted_Endemicity",
  "prediction_status", "predicted_at",
];
const SKIP_COLUMNS = new Set(["location", "lastUpdated", "Predicted_Risk"]);
// then the woreda row fields in the same order as Training Data.csv
const DHS_FIELDS = [
  "water_improved", "sanitation_improved", "open_defecation", "urban_share",
  "wealth_quintile", "round_year", "n_clusters", "n_households",
  "water_improved_trend", "sanitation_improved_trend", "prior_round_year",
];
const ROW_COLUMNS = [
  "lat", "lon", "location_name", "observation_date", "sample_size", "geo_precision",
  "elevation_m", "water_distance_m", "proximity_to_irrigation_km", "ndvi_index",
  "ndwi_index", "proximity_to_settlements_km", "temperature_mean_c", "rainfall_mm",
  "humidity_pct", "population_density", "school_age_ratio", "snail_habitat_presence",
  "water_body_type", "soil_type", "rainfall_cumulative_3m", "rainfall_cumulative_12m",
  "temperature_mean_3m", "rainfall_seasonal_anomaly_3m", "ndvi_trend_2y", "ndwi_trend_2y",
  "mda_years_since_start", "mda_rounds_effective", "prevalence_pct",
  ...["10km", "15km", "25km"].flatMap((r) => DHS_FIELDS.map((f) => `dhs_${r}_${f}`)),
  "water_distance_m_source_type", "proximity_to_settlements_km_source_type",
  "population_reference_year", "survey_date", "population_data_lag_years",
  "missing_features_imputed",
];

// sites: from the backend (matched by woreda name); woredas: from their
// location_id "region-slug/zone-slug/woreda-slug"
const regionOf = (r) => r.region || (r.location_id || "").split("/")[0]?.replace(/-/g, " ") || "";
const zoneOf = (r) => r.zone || (r.location_id || "").split("/")[1]?.replace(/-/g, " ") || "";
const isSite = (r) => r["Sr No"] != null;

function csvCell(v) {
  if (v === null || v === undefined) return "";
  const s = typeof v === "object" ? JSON.stringify(v) : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadCsv(rows) {
  const withNames = rows.map((r) => ({
    ...r,
    zone: zoneOf(r),
    region: regionOf(r),
    Predicted_Endemicity: endemicityOf(r.Predicted_Prevalence_pct),
    prediction_status: PREDICTION_STATUS,
    predicted_at: predictedAt(r),
  }));
  const rest = new Set();
  withNames.forEach((r) =>
    Object.keys(r).forEach((k) => {
      if (!LEADING_COLUMNS.includes(k) && !SKIP_COLUMNS.has(k)) rest.add(k);
    })
  );
  const ordered = ROW_COLUMNS.filter((c) => rest.has(c));
  const columns = [...LEADING_COLUMNS, ...ordered, ...[...rest].filter((c) => !ROW_COLUMNS.includes(c))];
  const lines = [
    columns.join(","),
    ...withNames.map((r) => columns.map((c) => csvCell(r[c])).join(",")),
  ];
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `predictions_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// every stored field, in display order for the row detail popup
const pctOf = (v) => (v === null || v === undefined ? "—" : `${(v * 100).toFixed(1)}%`);
const yesNo = (v) => (v === null || v === undefined ? "—" : v ? "Yes" : "No");
const text = (v) => (v === null || v === undefined || v === "" ? "—" : String(v));
// stored but not shown in the popup
const HIDDEN_IN_POPUP = new Set(["region", "zone"]);
const DETAIL_GROUPS = [
  {
    title: "Survey",
    fields: [
      ["Sr No", "Sr No (Training Data.csv)", text],
      ["prevalence_pct", "Surveyed prevalence %", (v) => fmt(v, 1, "%")],
      ["sample_size", "People tested", text],
      ["survey_date", "Survey date", text],
    ],
  },
  {
    title: "Model prediction",
    fields: [
      ["Predicted_Prevalence_pct", "Predicted prevalence %", (v) => fmt(v, 1, "%")],
      ["Predicted_Reason", "Why", text],
      ["Predicted_Endemicity", "Predicted endemicity", text],
      ["prediction_status", "Status", text],
      ["predicted_at", "Prediction date", fmtDate],
    ],
  },
  {
    title: "Data quality",
    fields: [
      ["missing_features_imputed", "Inputs filled with typical values", (v) => <ImputedNote count={v} />],
      ["geo_precision", "Geo precision", text],
      ["observation_date", "Data as of", text],
      ["population_data_lag_years", "Population data lag", (v) => fmt(v, 0, " yrs")],
    ],
  },
  {
    title: "Location",
    fields: [
      ["woreda", "Woreda", text],
      ["location_name", "Location name", text],
      ["location_id", "Location ID", text],
      ["lat", "Latitude", (v) => fmt(v, 5)],
      ["lon", "Longitude", (v) => fmt(v, 5)],
    ],
  },
  {
    title: "Climate",
    fields: [
      ["elevation_m", "Elevation", (v) => fmt(v, 0, " m")],
      ["temperature_mean_c", "Mean temperature", (v) => fmt(v, 2, " °C")],
      ["temperature_mean_3m", "Mean temperature (last 3 months)", (v) => fmt(v, 2, " °C")],
      ["rainfall_mm", "Rainfall (last 12 months)", (v) => fmt(v, 1, " mm")],
      ["rainfall_cumulative_3m", "Rainfall (last 3 months)", (v) => fmt(v, 1, " mm")],
      ["rainfall_cumulative_12m", "Rainfall cumulative (12 months)", (v) => fmt(v, 1, " mm")],
      ["rainfall_seasonal_anomaly_3m", "Rainfall anomaly (3 months vs normal)", (v) => fmt(v, 1, " mm")],
      ["humidity_pct", "Humidity", (v) => fmt(v, 1, "%")],
    ],
  },
  {
    title: "Water, vegetation & soil",
    fields: [
      ["water_distance_m", "Nearest water body", (v) => fmt(v, 0, " m")],
      ["water_body_type", "Water body type", text],
      ["water_distance_m_source_type", "Water distance source", text],
      ["proximity_to_irrigation_km", "Nearest irrigation", (v) => (v == null ? "None within 25 km" : fmt(v, 2, " km"))],
      ["ndvi_index", "NDVI (vegetation)", (v) => fmt(v, 3)],
      ["ndvi_trend_2y", "NDVI trend (per year, 2 yrs)", (v) => fmt(v, 4)],
      ["ndwi_index", "NDWI (surface water)", (v) => fmt(v, 3)],
      ["ndwi_trend_2y", "NDWI trend (per year, 2 yrs)", (v) => fmt(v, 4)],
      ["soil_type", "Soil type", text],
      ["snail_habitat_presence", "Snail habitat likely", yesNo],
    ],
  },
  {
    title: "Population",
    fields: [
      ["population_density", "Population density", (v) => fmt(v, 1, " /km²")],
      ["school_age_ratio", "School-age (5–14) share", pctOf],
      ["proximity_to_settlements_km", "Nearest settlement", (v) => fmt(v, 2, " km")],
      ["proximity_to_settlements_km_source_type", "Settlement distance source", text],
      ["population_reference_year", "Population data year", text],
    ],
  },
  {
    title: "Treatment (MDA)",
    fields: [
      ["mda_rounds_effective", "Effective MDA rounds", text],
      ["mda_years_since_start", "Years since MDA started", text],
    ],
  },
  ...["10km", "15km", "25km"].map((r) => ({
    title: `DHS WASH & wealth (${r})`,
    fields: [
      [`dhs_${r}_water_improved`, "Improved drinking water", pctOf],
      [`dhs_${r}_sanitation_improved`, "Improved sanitation", pctOf],
      [`dhs_${r}_open_defecation`, "Open defecation", pctOf],
      [`dhs_${r}_urban_share`, "Urban share", pctOf],
      [`dhs_${r}_wealth_quintile`, "Mean wealth quintile (1–5)", (v) => fmt(v, 2)],
      [`dhs_${r}_water_improved_trend`, "Improved water trend", pctOf],
      [`dhs_${r}_sanitation_improved_trend`, "Improved sanitation trend", pctOf],
      [`dhs_${r}_round_year`, "DHS round", text],
      [`dhs_${r}_prior_round_year`, "Prior DHS round", text],
      [`dhs_${r}_n_clusters`, "Clusters", text],
      [`dhs_${r}_n_households`, "Households", text],
    ],
  })),
];

function DetailModal({ row, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const endemicity = endemicityOf(row.Predicted_Prevalence_pct);
  row = { ...row, Predicted_Endemicity: endemicity, prediction_status: PREDICTION_STATUS, predicted_at: predictedAt(row) };
  const known = new Set(DETAIL_GROUPS.flatMap((g) => g.fields.map(([k]) => k)));
  const other = Object.keys(row).filter(
    (k) => !known.has(k) && !SKIP_COLUMNS.has(k) && !HIDDEN_IN_POPUP.has(k)
  );
  // one flat list: all fields in group order, then anything new
  const fields = [
    ...DETAIL_GROUPS.filter((g) => g.title !== "Survey" || isSite(row)).flatMap((g) => g.fields),
    ...other.map((k) => [k, k, text]),
  ];

  return (
    <div className="rp-modal-backdrop" onClick={onClose}>
      <div className="rp-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="rp-modal__head rp-modal__head--stack">
          <div className="rp-modal__top">
            <div className="rp-modal__title">
              <h2>{isSite(row) ? row.location_name || row.woreda : row.woreda}</h2>
              <p className="rp-muted rp-capitalize">
                {[isSite(row) && row.woreda, zoneOf(row), regionOf(row)].filter(Boolean).join(" · ")}
              </p>
            </div>
            <div className="rp-modal__actions">
              <button className="rp-download" onClick={() => downloadCsv([row])}>
                <FiDownload />
                CSV
              </button>
              <button className="rp-modal__close" onClick={onClose} aria-label="Close">
                <FiX />
              </button>
            </div>
          </div>
          <div className="rp-modal__stats">
            {isSite(row) && (
              <div className="rp-stat">
                <span className="rp-stat__cap">Surveyed</span>
                <span className="rp-stat__value">{fmt(row.prevalence_pct, 1, "%")}</span>
              </div>
            )}
            <div className="rp-stat">
              <span className="rp-stat__cap">Predicted</span>
              <span className="rp-stat__value">
                {fmt(row.Predicted_Prevalence_pct, 1, "%")}
                <EndemicityBadge label={endemicity} />
              </span>
            </div>
            <div className="rp-modal__notes">
              <span className="rp-exp-tag">{PREDICTION_STATUS}</span>
              <ImputedNote count={row.missing_features_imputed} />
            </div>
          </div>
        </div>
        <div className="rp-modal__body">
          <table className="rp-modal__table">
            <thead>
              <tr>
                <th>Field</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              {fields.map(([key, label, show]) => (
                <tr key={key}>
                  <td>
                    {label}
                    <span className="rp-modal__key">{key}</span>
                  </td>
                  <td>{show(row[key])}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ExperimentalBanner() {
  return (
    <div className="rp-exp" role="note">
      <FiAlertTriangle />
      <div>
        <b>{PREDICTION_STATUS}</b>
        <span>
          These are model estimates, not survey results. They are being checked against new field surveys and must
          not be used on their own to plan or stop treatment.
        </span>
      </div>
    </div>
  );
}

function EndemicityBadge({ label }) {
  return (
    <span className="rp-badge" style={BADGE_STYLES[label] || NO_DATA_STYLE}>
      {label || "—"}
    </span>
  );
}

export default function RiskPrediction() {
  const [params, setParams] = useSearchParams();
  const [sites, setSites] = useState([]);
  const [woredas, setWoredas] = useState([]);
  const [model, setModel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState(params.get("q") || "");
  const [riskFilter, setRiskFilter] = useState("");
  const [selected, setSelected] = useState(null);
  // region / zone come from the URL, so links from other pages (and the
  // browser back button) land on the same view
  // ?view=sites: the surveyed sites; otherwise the woreda rows
  const view = params.get("view") === "sites" ? "sites" : "woredas";
  const rows = view === "woredas" ? woredas : sites;
  const noun = view === "woredas" ? "woredas" : "sites";
  const setView = (v) => {
    const p = new URLSearchParams(params);
    if (v === "sites") p.set("view", v);
    else p.delete("view");
    ["region", "zone", "woreda"].forEach((k) => p.delete(k));
    setParams(p);
    setSearch("");
    setRiskFilter("");
  };
  const region = params.get("region") || "";
  const zone = params.get("zone") || "";
  const setScope = (next) => {
    const p = new URLSearchParams(params);
    ["region", "zone", "woreda"].forEach((k) => (next[k] ? p.set(k, next[k]) : p.delete(k)));
    setParams(p);
  };

  useEffect(() => {
    async function load() {
      const byPct = (a, b) => (b.Predicted_Prevalence_pct ?? -1) - (a.Predicted_Prevalence_pct ?? -1);
      const [s, w] = await Promise.allSettled([getSurveySites(), getWoredaRiskPredictions()]);
      if (s.status === "fulfilled") setSites(s.value.sort(byPct));
      if (w.status === "fulfilled") setWoredas(w.value.sort(byPct));
      const failed = [s.status === "rejected" && "training data", w.status === "rejected" && "woreda predictions"].filter(Boolean);
      if (failed.length) setError(`Failed to load the ${failed.join(" and ")}`);
      setLoading(false);
    }
    load();
    getRiskModelInfo().then(setModel).catch(() => setModel(null));
  }, []);

  // ?woreda=Name (e.g. from Hotspot Mapping) searches for that woreda
  const woredaParam = params.get("woreda");
  useEffect(() => {
    if (woredaParam) setSearch(woredaParam);
  }, [woredaParam]);

  const regions = useMemo(() => [...new Set(rows.map(regionOf).filter(Boolean))].sort(), [rows]);
  const zones = useMemo(
    () => [...new Set(rows.filter((r) => !region || regionOf(r) === region).map(zoneOf).filter(Boolean))].sort(),
    [rows, region]
  );
  // rows in the chosen region / zone: the summary cards count these
  const scoped = useMemo(
    () => rows.filter((r) => (!region || regionOf(r) === region) && (!zone || zoneOf(r) === zone)),
    [rows, region, zone]
  );

  const counts = useMemo(() => {
    const c = Object.fromEntries(ENDEMICITY.map((e) => [e.label, 0]));
    scoped.forEach((r) => {
      const e = endemicityOf(r.Predicted_Prevalence_pct);
      if (e) c[e] += 1;
    });
    return c;
  }, [scoped]);

  const avgPrevalence = useMemo(() => {
    const v = scoped.map((r) => r.Predicted_Prevalence_pct).filter((x) => x != null);
    return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null;
  }, [scoped]);

  // data quality: rows where some model inputs were missing and filled
  const withImputed = useMemo(() => scoped.filter((r) => r.missing_features_imputed > 0).length, [scoped]);

  const perf = model?.performance;
  // shown numbers: Leave-One-Woreda-Out
  const lowo = perf?.lowo;
  const accuracy = accuracyValue(perf);
  // sites: made now by the current model; woredas: when they were loaded
  const predictedOn = rows.map(predictedAt).filter(Boolean).sort().pop();

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return scoped.filter(
      (r) =>
        (!riskFilter || endemicityOf(r.Predicted_Prevalence_pct) === riskFilter) &&
        (!q ||
          (r.woreda || "").toLowerCase().includes(q) ||
          (r.location_name || "").toLowerCase().includes(q))
    );
  }, [scoped, search, riskFilter]);

  return (
    <div className="rp-container">
      <Header />

      <div className="rp-body">
        <ExperimentalBanner />

        {/* Model version, prediction date, performance */}
        <div className="rp-views" role="tablist">
          <button role="tab" aria-selected={view === "woredas"} className={view === "woredas" ? "active" : ""} onClick={() => setView("woredas")}>
            Woredas <span>{woredas.length}</span>
          </button>
          <button role="tab" aria-selected={view === "sites"} className={view === "sites" ? "active" : ""} onClick={() => setView("sites")}>
            Training data <span>{sites.length}</span>
          </button>
        </div>

        {/* Model version, dates, data quality, performance */}
        <div className="rp-panel rp-model">
          <div className="rp-model__facts">
            <div className="rp-fact">
              <span className="rp-fact__cap">Model</span>
              <span className="rp-fact__value">
                {model?.model || "schisto_risk_model_baseline"}
                <code className="rp-model__ver" title={model?.file_hash ? `File hash ${model.file_hash}` : undefined}>
                  v {model?.version || "—"}
                </code>
              </span>
            </div>
            <div className="rp-fact">
              <span className="rp-fact__cap">Model saved</span>
              <span className="rp-fact__value">{fmtDate(model?.saved_at || model?.trained_at)}</span>
            </div>
            <div className="rp-fact">
              <span className="rp-fact__cap">Predictions made</span>
              <span className="rp-fact__value">{fmtDate(predictedOn)}</span>
            </div>
            <div className="rp-fact">
              <span className="rp-fact__cap">Data quality</span>
              <span className="rp-fact__value">
                {withImputed} / {scoped.length} {noun}
                <span className="rp-muted rp-fact__sub"> had missing inputs filled</span>
              </span>
              <span className={`rp-bar ${scoped.length && withImputed / scoped.length > 0.5 ? "warn" : ""}`}>
                <span style={{ width: `${scoped.length ? (100 * withImputed) / scoped.length : 0}%` }} />
              </span>
            </div>
          </div>

          <div className="rp-perf">
            <div className="rp-perf__head">
              <div>
                <h4>Model performance</h4>
                <span className="rp-muted">
                  {lowo
                    ? `${lowo.locations} training data sites in ${lowo.woredas} woredas · measured ${fmtDate(perf.measured_at)}`
                    : "Not measured yet: measure accuracy again on Model Training"}
                </span>
              </div>
              {perf?.outdated && (
                <span className="rp-pill rp-pill--warn">
                  <FiAlertTriangle /> Measured on an earlier model version
                </span>
              )}
            </div>
            <div className="rp-perf__cards">
              <div className="rp-metric rp-metric--blue">
                <span className="rp-metric__value">{lowo ? lowo.mae_pct_points.toFixed(1) : "—"}<small> pts</small></span>
                <span className="rp-metric__label">Mean absolute error (MAE) <em className="rp-test">LOWO</em></span>
                <span className="rp-metric__note">Average gap between predicted and surveyed prevalence</span>
              </div>
              <div className="rp-metric rp-metric--cyan">
                <span className="rp-metric__value">{lowo ? lowo.rmse_pct_points.toFixed(1) : "—"}<small> pts</small></span>
                <span className="rp-metric__label">Root mean squared error (RMSE) <em className="rp-test">LOWO</em></span>
                <span className="rp-metric__note">
                  Like mean absolute error, but big misses count much more than small ones, so a value well above the
                  mean absolute error means a few sites are missed by a lot
                </span>
              </div>
              <div className="rp-metric rp-metric--violet">
                <span className="rp-metric__value">{lowo ? lowo.same_level_pct.toFixed(1) : "—"}<small>%</small></span>
                <span className="rp-metric__label">Same risk level <em className="rp-test">LOWO</em></span>
                <span className="rp-metric__note">Predicted and surveyed in the same level: Low &lt;10%, Moderate 10–49.9%, High ≥50%</span>
              </div>
              <div className="rp-metric rp-metric--green">
                <span className="rp-metric__value">{accuracy != null ? accuracy.toFixed(1) : "—"}<small>%</small></span>
                <span className="rp-metric__label">Accuracy <em className="rp-test">LOWO</em><em className="rp-test">by MAE</em></span>
                <span className="rp-metric__note">{ACCURACY_NOTE}</span>
              </div>
            </div>
            <p className="rp-perf__foot">
              LOWO (Leave-One-Woreda-Out): all sites of each woreda are predicted by a model trained on the other
              woredas only, a woreda the model has never seen. Accuracy: each site is predicted by a model that did not
              train on that site. Retrospective, on the survey area only.
              {view === "sites" &&
                " The table shows the current model, same as Model Training; it learned these sites, so they match better than a new place would."}
            </p>
          </div>
        </div>

        {/* Region > Zone > Woreda */}
        <nav className="rp-crumbs" aria-label="Area">
          <button className={!region ? "active" : ""} onClick={() => setScope({})}>All regions</button>
          {region && (
            <>
              <FiChevronRight />
              <button className={`rp-capitalize ${!zone ? "active" : ""}`} onClick={() => setScope({ region })}>
                {region}
              </button>
            </>
          )}
          {zone && (
            <>
              <FiChevronRight />
              <span className="rp-capitalize active">{zone}</span>
            </>
          )}
        </nav>

        {/* Summary */}
        <div className="rp-summary">
          <div className="rp-card">
            <span className="rp-card__label">{view === "sites" ? "Training data" : "Woredas"}</span>
            <span className="rp-card__value">{scoped.length}</span>
          </div>
          {ENDEMICITY.map(({ label }) => (
            <button
              key={label}
              className={`rp-card rp-card--button ${riskFilter === label ? "active" : ""}`}
              style={{ borderTopColor: colorOf(label) }}
              onClick={() => setRiskFilter(riskFilter === label ? "" : label)}
            >
              <span className="rp-card__label">{label}</span>
              <span className="rp-card__value">{counts[label]}</span>
            </button>
          ))}
          <div className="rp-card">
            <span className="rp-card__label">Average predicted prevalence %</span>
            <span className="rp-card__value">{fmt(avgPrevalence, 1, "%")}</span>
          </div>
        </div>

        {/* Table */}
        <div className="rp-panel rp-table-panel">
          <div className="rp-table-toolbar">
            <h3>
              {view === "sites" ? "Training data" : "Woreda predictions"}{" "}
              <span className="rp-muted">
                ({filtered.length}
                {filtered.length !== rows.length ? ` of ${rows.length}` : ""})
              </span>
            </h3>
            <div className="rp-table-controls">
              <select value={region} onChange={(e) => setScope({ region: e.target.value })} aria-label="Region">
                <option value="">All regions</option>
                {regions.map((r) => (
                  <option key={r} value={r} className="rp-capitalize">{r}</option>
                ))}
              </select>
              <select value={zone} onChange={(e) => setScope({ region, zone: e.target.value })} aria-label="Zone">
                <option value="">All zones</option>
                {zones.map((z) => (
                  <option key={z} value={z}>{z}</option>
                ))}
              </select>
              <input
                type="text"
                placeholder={view === "sites" ? "Search site or woreda…" : "Search woreda…"}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <select value={riskFilter} onChange={(e) => setRiskFilter(e.target.value)}>
                <option value="">All endemicity categories</option>
                {ENDEMICITY.map(({ label }) => (
                  <option key={label} value={label}>{label}</option>
                ))}
              </select>
              <button
                className="rp-download"
                onClick={() => downloadCsv(filtered)}
                disabled={!filtered.length}
                title={`Download every field for the ${noun} in the table`}
              >
                <FiDownload />
                Download all details (CSV)
              </button>
            </div>
          </div>
          {error && <p className="rp-error">{error}</p>}
          <div className="rp-table-wrap">
            <table className="rp-table">
              <thead>
                <tr>
                  {view === "sites" && <th>Site</th>}
                  <th>Woreda</th>
                  <th>Zone</th>
                  <th>Region</th>
                  {view === "sites" && <th className="num">Surveyed %</th>}
                  <th className="num">Predicted %</th>
                  <th>Predicted endemicity</th>
                  {view === "sites" && <th>Survey date</th>}
                  <th>Inputs filled with typical values</th>
                  <th className="num">Elevation</th>
                  <th className="num">Temperature</th>
                  <th className="num">Nearest water</th>
                  <th className="num">Population /km²</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr
                    key={`${r["Sr No"] ?? ""}-${r.location_id}`}
                    className="rp-clickable"
                    onClick={() => setSelected(r)}
                    title={`Click to see all data for this ${view === "sites" ? "site" : "woreda"}`}
                  >
                    {view === "sites" && <td><b>{r.location_name || "—"}</b></td>}
                    <td>{view === "sites" ? r.woreda : <b>{r.woreda}</b>}</td>
                    <td className="rp-capitalize">
                      <button
                        className="rp-linkish"
                        onClick={(e) => {
                          e.stopPropagation();
                          setScope({ region: regionOf(r), zone: zoneOf(r) });
                        }}
                        title="Show only this zone"
                      >
                        {zoneOf(r)}
                      </button>
                    </td>
                    <td className="rp-capitalize">
                      <button
                        className="rp-linkish"
                        onClick={(e) => {
                          e.stopPropagation();
                          setScope({ region: regionOf(r) });
                        }}
                        title="Show only this region"
                      >
                        {regionOf(r)}
                      </button>
                    </td>
                    {view === "sites" && <td className="num">{fmt(r.prevalence_pct, 1, "%")}</td>}
                    <td className="num">{fmt(r.Predicted_Prevalence_pct, 1, "%")}</td>
                    <td>
                      <EndemicityBadge label={endemicityOf(r.Predicted_Prevalence_pct)} />
                    </td>
                    {view === "sites" && <td>{r.survey_date || r.observation_date || "—"}</td>}
                    <td>
                      <ImputedNote count={r.missing_features_imputed} short />
                    </td>
                    <td className="num">{fmt(r.elevation_m, 0, " m")}</td>
                    <td className="num">{fmt(r.temperature_mean_c, 1, " °C")}</td>
                    <td className="num">
                      {r.water_distance_m == null ? "—" : fmt(r.water_distance_m / 1000, 1, " km")}
                    </td>
                    <td className="num">{fmt(r.population_density, 0)}</td>
                  </tr>
                ))}
                {!loading && filtered.length === 0 && (
                  <tr>
                    <td colSpan={13} className="rp-empty">No {noun} match.</td>
                  </tr>
                )}
                {loading && (
                  <tr>
                    <td colSpan={13} className="rp-empty">Loading…</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {selected && <DetailModal row={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
